import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LBtn, LModal, LModalFooterButtons } from "@components";
import { longLocalizedDate } from "@helpers/dateHelpers";
import { parseStatus } from "@helpers/enumHelper";
import { getDetails } from "@helpers/examineeHelper";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { useApi } from "@services/useApi";
import { AssessmentHistory, ExistingExamineeDetails } from "@store/generalTypes";
import { UpcomingAssessment } from "@store/locationsSlice";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { setAssessmentHistoryByExaminee } from "../../store/assessmentContextSlice";
import { ExamineeContactInfo, updateContactInfo } from "../../store/newExamineeSlice";
import { ExamineeCard } from "../ExamineeCard";
import { EditExaminee } from "./EditExaminee";
import { StartAssessmentModal } from "./start-assessment/StartAssessmentModal";

export const RightSide = ({ upcomingAssessment, completed }: RightSideProps): JSX.Element => {
    const [selectedExamineeDetails, setSelectedExamineeDetails] = useState<ExistingExamineeDetails>();
    const [beginAssessmentOpen, setBeginAssessmentOpen] = useState(false);
    const [editExamineeOpen, setEditExamineeOpen] = useState(false);

    const { getExamineeDetails, loading, updateExaminee, getAssessmentHistoryByExaminee } = useApi();

    const dispatch = useAppDispatch();

    const { location, assessmentContext, organizationAssessments } = useAppSelector((x) => {
        return {
            location: x.orgLocations.currentLocation,
            assessmentContext: x.assessmentContext,
            organizationAssessments: x.organizationAssessments
        };
    });

    const { isFacilitator } = useUserPermission({ useCurrentLocation: true });

    const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistory[]>();

    const handleGetExamineeDetails = async (): Promise<void> => {
        const examinee = await getExamineeDetails(location!.id, Number(upcomingAssessment!.examineeId));
        setSelectedExamineeDetails(examinee);
    };

    const toggleBeginAssessment = (): void => {
        const contactInfo: ExamineeContactInfo = {
            firstName: selectedExamineeDetails!.firstName,
            lastName: selectedExamineeDetails!.lastName,
            id: 0,
            phoneNumber: ""
        };
        dispatch(updateContactInfo(contactInfo));

        setBeginAssessmentOpen(!beginAssessmentOpen);
    };

    const handleOnStartAssessmentClose = (): void => {
        toggleBeginAssessment();
    };

    const toggleEditExaminee = (): void => {
        setEditExamineeOpen(!editExamineeOpen);
    };

    const confirmEditExaminee = (): void => {
        void handleGetExamineeDetails();
        setEditExamineeOpen(false);
    };

    const loadHistory = useCallback(async () => {
        if (upcomingAssessment && isEmpty(assessmentContext.examineeAssessmentHistory[upcomingAssessment.id])) {
            const history = await getAssessmentHistoryByExaminee(location!.id, upcomingAssessment.examineeId);

            dispatch(
                setAssessmentHistoryByExaminee({
                    examineeId: upcomingAssessment.examineeId,
                    assessmentHistory: history
                })
            );

            setAssessmentHistory(history);
        }
    }, [location?.id, upcomingAssessment?.id]);

    useEffect(() => {
        setSelectedExamineeDetails(undefined);

        if (upcomingAssessment?.id) {
            void handleGetExamineeDetails();
            void loadHistory();
        }
    }, [upcomingAssessment?.id]);

    if (!upcomingAssessment) {
        return <div className="column has-background-white is-bordered-top is-bordered-bottom" />;
    }

    const examineeContactInfo: ExamineeContactInfo = {
        id: upcomingAssessment.examineeId,
        firstName: upcomingAssessment?.examineeFirstName || (selectedExamineeDetails?.firstName ?? ""),
        lastName: upcomingAssessment?.examineeLastName || (selectedExamineeDetails?.lastName ?? ""),
        email: upcomingAssessment?.examineeEmail || selectedExamineeDetails?.email,
        phoneNumber: upcomingAssessment?.examineePhoneNumber || (selectedExamineeDetails?.phoneNumber ?? "")
    };

    return (
        <>
            <div className="column has-background-white is-bordered-top is-bordered-bottom">
                <ExamineeCard
                    contactInfo={examineeContactInfo}
                    bgColor="#fff"
                    loading={loading}
                    classList="p-5"
                    examineeDetails={selectedExamineeDetails && getDetails(selectedExamineeDetails)}
                    bigLabels
                    clinician={
                        selectedExamineeDetails &&
                        `${selectedExamineeDetails?.clinicianFirstName} ${selectedExamineeDetails?.clinicianLastName}`
                    }
                    onEdit={toggleEditExaminee}
                />

                <div className="is-flex is-flex-direction-column mx-5">
                    {!completed && isFacilitator && (
                        <>
                            <div className="divider" />
                            <div style={{ maxWidth: "50%" }}>
                                <LBtn
                                    smallSpacing
                                    classes="px-5"
                                    onClick={toggleBeginAssessment}
                                    isBold
                                    disabled={!organizationAssessments.hasAssessment}
                                    toolTipText={
                                        "Your account is out of assessments. More can be purchased in the \"Manage\" section of the LASSI-D™ portal."
                                    }
                                >
                                    Begin Assessment
                                </LBtn>
                            </div>
                        </>
                    )}
                    {completed && isFacilitator && (
                        <>
                            <div className="divider" />
                            <div style={{ maxWidth: "50%" }}>
                                <LBtn
                                    smallSpacing
                                    classes="px-5"
                                    onClick={toggleBeginAssessment}
                                    disabled={!organizationAssessments.hasAssessment}
                                    toolTipText={
                                        "Your account is out of assessments. More can be purchased in the \"Manage\" section of the LASSI-D™ portal."
                                    }
                                >
                                    New Assessment
                                </LBtn>
                            </div>
                            <p className="has-text-weight-semibold has-text-grey my-4">
                                Allow for at least 6 months between assessments
                            </p>
                        </>
                    )}
                </div>

                {!isEmpty(assessmentHistory) && (
                    <>
                        <div className="divider" />
                        <div className="is-flex is-flex-direction-column mx-5 mb-4">
                            <p className="has-text-weight-semibold has-text-grey mb-4">HISTORY</p>
                            {assessmentHistory?.map((hs, idx) => {
                                return (
                                    <div
                                        className="columns is-flex is-flex-direction-row is-align-items-center"
                                        key={`history-${hs.displayId}-${idx}`}
                                    >
                                        <div className="column is-3">
                                            <p className="has-text-weight-semibold has-text-ldarkgrey">
                                                {longLocalizedDate(hs.launchedDate, "P")}
                                            </p>
                                        </div>
                                        <div className="column is-flex is-flex-direction-row">
                                            {hs.assessmentFormName}
                                            <div className="mx-2">-</div>
                                            <p className="has-text-weight-semibold">{parseStatus(hs.status)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* START ASSESSMENT */}
            {beginAssessmentOpen && (
                <StartAssessmentModal
                    examinee={examineeContactInfo}
                    locationId={Number(location?.id)}
                    upcomingAssessmentId={completed ? undefined : upcomingAssessment.id}
                    onClose={handleOnStartAssessmentClose}
                />
            )}

            {/* EDIT EXAMINEE */}
            {editExamineeOpen && (
                <LModal
                    onClose={toggleEditExaminee}
                    onConfirm={confirmEditExaminee}
                    footer={<LModalFooterButtons confirmText="Save" cancelText="Cancel" centered loading={loading} />}
                    title={<p className="has-text-centered has-text-weight-bold is-size-3">Edit Examinee Info</p>}
                    width={"m"}
                >
                    <EditExaminee
                        contactInfo={examineeContactInfo}
                        details={selectedExamineeDetails}
                        handleUpdate={updateExaminee}
                    />
                </LModal>
            )}
        </>
    );
};

interface RightSideProps {
    upcomingAssessment?: UpcomingAssessment;
    completed?: boolean;
}
